/* =============================================================================
   VIEWS / ADMIN / DAY-CLOSE-RENDER — Tab-Render + Toolbars + Setters + Exports
   =============================================================================
   v0.76.0-beta / Session 70 / REF-5b Cut 2/4
   ----------------------------------------------------------------------------
   Tab-Render-Pipeline für den Tagesabschluss-Tab. Stichtag-Toolbar + Export-
   Toolbar + Sync/Async-Receipt-Renderer + DATEV-/DSFinV-K-Exporte.
============================================================================ */

import { htmlEscape, todayISO, fmtEUR } from '../../utils/helpers.js';
import { exportOrdersDatevCsv } from '../../utils/datev-export.js';
import { exportDsfinvk } from '../../utils/dsfinvk-export.js';
import { renderDayCloseReceipt, renderDayCloseReceiptFresh, printDayCloseReceipt, dayCloseBookingDate } from './receipt.js';
import { _state, getState, resolveExportRange, fmtRangeDE } from './day-close-state.js';
import { getOpenUnpaidDineInOrders, sumOrderGrossCents, orderGrossCents } from './orders-data.js';

/* ----------------------------------------------------------------------------- */
/* Toolbar                                                                        */
/* ----------------------------------------------------------------------------- */

function renderToolbar(activeDate) {
  return `
    <div class="day-close-toolbar no-print">
      <div class="day-close-toolbar-left">
        <label for="day-close-date">Stichtag (Z-Bon):</label>
        <input id="day-close-date" type="date" value="${htmlEscape(activeDate)}" onchange="setDayCloseDate(this.value)">
        <button type="button" class="btn btn-ghost btn-sm" onclick="setDayCloseDate('${htmlEscape(todayISO())}')">Heute</button>
      </div>
      <div class="day-close-toolbar-right">
        <button type="button" class="btn btn-primary btn-sm" onclick="startDayCloseZBon()">💾 Z-Bon ausgeben</button>
      </div>
    </div>
  `;
}

function renderExportToolbar() {
  const range = resolveExportRange();
  const m = _state.exportMode;
  const btn = (mode, label) => {
    const active = mode === m ? ' active' : '';
    return `<button type="button" class="btn btn-ghost btn-sm period-chip${active}" onclick="setDayCloseExportMode('${mode}')">${label}</button>`;
  };
  const customFields = m === 'custom' ? `
    <span style="display:inline-flex; align-items:center; gap:8px; margin-left:12px;">
      <label for="dc-export-from" style="font-size:.85rem; color: var(--ink-soft);">von</label>
      <input id="dc-export-from" type="date" value="${htmlEscape(_state.exportFrom || range.from)}" onchange="setDayCloseExportCustom('from', this.value)">
      <label for="dc-export-to" style="font-size:.85rem; color: var(--ink-soft);">bis</label>
      <input id="dc-export-to" type="date" value="${htmlEscape(_state.exportTo || range.to)}" onchange="setDayCloseExportCustom('to', this.value)">
    </span>
  ` : '';

  return `
    <div class="day-close-export-bar no-print">
      <div class="day-close-export-row">
        <span class="day-close-export-label">📦 Exporte</span>
        <div class="period-chips">
          ${btn('day','Tag')}
          ${btn('month','Monat')}
          ${btn('year','Jahr')}
          ${btn('custom','Frei')}
        </div>
        ${customFields}
      </div>
      <div class="day-close-export-row day-close-export-row--meta">
        <span class="day-close-export-range">Zeitraum: <strong>${htmlEscape(fmtRangeDE(range))}</strong></span>
        <div class="day-close-export-actions">
          <button type="button" class="btn btn-secondary btn-sm" onclick="exportDayCloseDatev()">📊 DATEV-CSV</button>
          <button type="button" class="btn btn-secondary btn-sm" style="border-color: var(--c-accent); color: var(--c-accent);" onclick="exportDayCloseDsfinvk()">📄 Außenprüfungs-Export (DSFinV-K)</button>
        </div>
      </div>
      <p class="hint" style="margin: 8px 0 0; font-size: .82rem;">
        DATEV-CSV ist für den Steuerberater (12 Spalten, Excel-kompatibel).
        DSFinV-K ist die Pflicht-Schnittstelle für eine Außenprüfung
        (4 Bewegungs- + 4 Stammdaten-CSVs + index.xml in einem ZIP).
      </p>
    </div>
  `;
}

/* ----------------------------------------------------------------------------- */
/* S100-C — Tagesabschluss-Gate: offene Bestellungen sichtbar machen              */
/* ----------------------------------------------------------------------------- */

const _DC_OPEN_PIPELINE = ['pending', 'confirmed', 'preparing', 'ready'];
const _DC_STATUS_LABEL = {
  pending: 'Eingegangen', confirmed: 'Angenommen', preparing: 'In Zubereitung',
  ready: 'Bereit', picked_up: 'Abgeholt, unbezahlt'
};

/**
 * S100-C — Pure-Helper: offene Bestellungen, die einen Tagesabschluss am
 * Stichtag `dateIso` betreffen. „Offen" = lebende Pipeline (pending/confirmed/
 * preparing/ready) ODER abgeholt-aber-unbezahlt (`picked_up`, Bonloch-Schutz).
 * Datums-Grenze: GoBD-Buchungstag (`dayCloseBookingDate`) ≤ Stichtag. Bezahlte
 * + abgeholte, stornierte und No-Show-Orders sind abgeschlossen → raus.
 * Pure (keine DOM-/State-Zugriffe), damit testbar.
 */
export function openOrdersForDayClose(orders, dateIso) {
  if (!Array.isArray(orders) || !dateIso) return [];
  return orders.filter(o => {
    if (!o) return false;
    const paid = o.paymentStatus === 'paid' || o.paymentStatus === 'refunded';
    const isPipeline = _DC_OPEN_PIPELINE.includes(o.status);
    const isPickedUpUnpaid = o.status === 'picked_up' && !paid;
    if (!isPipeline && !isPickedUpUnpaid) return false;
    const booking = dayCloseBookingDate(o);
    return !!booking && booking <= dateIso;
  });
}

/**
 * S100-C — Render des Warn-Blocks beim Tagesabschluss. Listet offene
 * Bestellungen mit Quick-Actions (Sprung in den operativen Tab + Direkt-Storno).
 * **Blockt den Z-Bon NICHT** — nur sichtbarer, bewusster Hinweis. Leerer String
 * bei N=0 (kein Lärm, wenn der Tag sauber ist).
 */
function renderOpenOrdersWarning(activeDate) {
  const state = getState();
  const orders = (state && Array.isArray(state.orders)) ? state.orders : [];
  const open = openOrdersForDayClose(orders, activeDate)
    .slice()
    .sort((a, b) => String(dayCloseBookingDate(a)).localeCompare(String(dayCloseBookingDate(b))));
  if (!open.length) return '';

  const rows = open.map(o => {
    const num     = htmlEscape(String(o.orderNumber || o.order_number || '—'));
    const name    = htmlEscape(String(o.customerName || o.customer_name || '—'));
    const booking = htmlEscape(dayCloseBookingDate(o) || '—');
    const label   = htmlEscape(_DC_STATUS_LABEL[o.status] || o.status || '—');
    const id      = htmlEscape(String(o.id || ''));
    return `
      <li class="dc-open-order">
        <span class="dc-open-order-info">
          <strong>#${num}</strong> · ${name} · ${booking} ·
          <span class="dc-open-order-status">${label}</span>
        </span>
        <button type="button" class="btn btn-ghost btn-xs" onclick="orderCancel('${id}')">Stornieren</button>
      </li>`;
  }).join('');

  const n = open.length;
  return `
    <div class="day-close-open-warning no-print" role="alert">
      <div class="dc-open-warning-head">
        ⚠️ ${n} offene ${n === 1 ? 'Bestellung' : 'Bestellungen'} — bitte abschließen oder stornieren
      </div>
      <p class="dc-open-warning-hint">
        Diese Posten sind bis zum Stichtag noch nicht abgeschlossen (serviert&nbsp;+&nbsp;bezahlt)
        oder storniert. Der Z-Bon lässt sich trotzdem ausgeben — offene Bestellungen wandern dann
        in die „⚠️&nbsp;Überfällig"-Sektion des operativen Tabs (gehen also nicht verloren).
      </p>
      <ul class="dc-open-order-list">${rows}</ul>
      <div class="dc-open-warning-actions">
        <button type="button" class="btn btn-secondary btn-sm" onclick="switchTab('orders')">→ Im operativen Tab bearbeiten</button>
      </div>
    </div>
  `;
}

/**
 * S233 Ticket C — Start des Tagesabschlusses: NICHT-blockierender Hinweis auf
 * offene, unbezahlte Dine-In-Bestellungen (heute + Vortage) VOR der Z-Bon-Ausgabe.
 * Kein Hard-Block, keine Auto-Stornierung — der Mensch entscheidet. Bestätigt der
 * Operator (oder gibt es keine offenen Orders), öffnet der bestehende Z-Bon-Flow
 * (`openZBonReceiptActionModal`, unverändert). Z-Bon-Berechnung/TSE-Kette bleiben
 * unberührt. Async, damit `styledConfirm` awaitbar ist.
 */
export async function startDayCloseZBon() {
  try {
    const open = getOpenUnpaidDineInOrders();
    if (open.length && typeof window !== 'undefined' && typeof window.styledConfirm === 'function') {
      const sum = sumOrderGrossCents(open);
      const shown = open.slice(0, 8).map(o =>
        `#${o.orderNumber || o.order_number || '—'} · ${fmtEUR(orderGrossCents(o) / 100)}`).join('\n');
      const more = open.length > 8 ? `\n… und ${open.length - 8} weitere` : '';
      const res = await window.styledConfirm({
        title: 'Noch offene Bestellungen',
        body: `Es sind noch ${open.length} unbezahlte Tisch-Bestellung${open.length === 1 ? '' : 'en'} offen (Summe ${fmtEUR(sum / 100)}).\n\n${shown}${more}\n\nTrotzdem den Z-Bon ausgeben?`,
        danger: true,
        confirmLabel: 'Trotzdem ausgeben',
        cancelLabel: 'Abbrechen'
      });
      if (!res || !res.confirmed) return;
    }
  } catch (e) {
    // Defensive: der Unpaid-Check darf den Tagesabschluss nie verhindern.
    console.warn('[startDayCloseZBon] Unpaid-Check übersprungen (nicht-fatal):', e);
  }
  if (typeof window !== 'undefined' && typeof window.openZBonReceiptActionModal === 'function') {
    window.openZBonReceiptActionModal();
  }
}

/* ----------------------------------------------------------------------------- */
/* Render                                                                         */
/* ----------------------------------------------------------------------------- */

export function renderDayClose() {
  const el = document.getElementById('tab-day-close');
  if (!el) return;

  if (!_state.date) _state.date = todayISO();
  const activeDate = _state.date;

  // Receipt-Renderer aus dem zentralen Modul. Liefert ein vollständiges
  // <article class="receipt receipt-day-close"> inkl. Header, Pflichtangaben,
  // Übersicht, Steuer-Aufteilung, Bezahlmethoden und TSE-Footer.
  // M-11 (Session 43): die sync-Variante liest aus dem 30-Tage-Cache; für
  // historische Tage wird beim Tab-Activate die async-Variante (mit DB-
  // Fresh-Read) durch `loadAndRenderDayClose` aufgerufen.
  const body = `
    <div class="day-close-wrap">
      ${renderDayCloseReceipt(activeDate)}
    </div>
  `;

  el.innerHTML = `
    <h2 class="no-print" style="margin-bottom: 16px;">Tagesabschluss</h2>
    <p class="hint no-print" style="margin-bottom: 16px;">
      Z-Bon im Online-Layout — finanzamt-konform mit Belegpflichtangaben
      (§&nbsp;14 UStG / §&nbsp;6 KassenSichV). Nur produktive Bestellungen
      fließen in den Umsatz, Stornos und No-Shows werden separat
      ausgewiesen. «Drucken» öffnet den Browser-Druckdialog im 80&nbsp;mm-
      Bondrucker-Layout. Belegpflichtangaben kommen aus den Settings →
      «Belegpflichtangaben» — bitte vor dem Echtbetrieb hinterlegen.
    </p>
    ${renderToolbar(activeDate)}
    ${renderOpenOrdersWarning(activeDate)}
    ${body}
    ${renderExportToolbar()}
  `;
}

/**
 * Async-Wrapper (Session 43, M-11): bei jedem Aktivieren des
 * Tagesabschluss-Tabs UND nach Date-Wechsel wird der Z-Bon mit
 * DB-frischen Orders gerendert. Damit sind Z-Bons für historische
 * Daten (älter als der 30-Tage-Cache) korrekt — Memory
 * `feedback_gg_export_fresh_db` auf den Z-Bon-Renderer übertragen,
 * analog zu `loadAndRenderAuditLog` aus S42.
 *
 * Bei Render-Fehler (Storage-Ausfall) fällt der Wrapper still auf den
 * sync-Renderer (Cache) zurück — Bediener bekommt zumindest die
 * verfügbaren Daten zu sehen, statt eines weißen Tabs.
 */
export async function loadAndRenderDayClose() {
  const el = document.getElementById('tab-day-close');
  if (!el) {
    // Element noch nicht im DOM (frühe Boot-Phase) — sync-Renderer
    // ist No-Op-safe und bricht selbst sauber ab.
    renderDayClose();
    return;
  }
  if (!_state.date) _state.date = todayISO();
  const activeDate = _state.date;

  // 1) Sofort die Toolbar + Loading-State rendern — kein leerer Tab in der
  //    Übergangsphase (Memory `feedback_gg_no_view_hijack`).
  el.innerHTML = `
    <h2 class="no-print" style="margin-bottom: 16px;">Tagesabschluss</h2>
    <p class="hint no-print" style="margin-bottom: 16px;">
      Z-Bon im Online-Layout — finanzamt-konform mit Belegpflichtangaben
      (§&nbsp;14 UStG / §&nbsp;6 KassenSichV). Nur produktive Bestellungen
      fließen in den Umsatz, Stornos und No-Shows werden separat
      ausgewiesen. «Drucken» öffnet den Browser-Druckdialog im 80&nbsp;mm-
      Bondrucker-Layout. Belegpflichtangaben kommen aus den Settings →
      «Belegpflichtangaben» — bitte vor dem Echtbetrieb hinterlegen.
    </p>
    ${renderToolbar(activeDate)}
    ${renderOpenOrdersWarning(activeDate)}
    <div class="day-close-wrap"><div class="hint">Lade Z-Bon aus der Datenbank …</div></div>
    ${renderExportToolbar()}
  `;

  // 2) DB-Fresh-Read + Render. Bei Fehler still auf Cache-Variante zurück.
  try {
    const html = await renderDayCloseReceiptFresh(activeDate);
    const wrap = el.querySelector('.day-close-wrap');
    if (wrap) wrap.innerHTML = html;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[day-close] DB-Fresh-Render fehlgeschlagen, fallback auf Cache:', err);
    const wrap = el.querySelector('.day-close-wrap');
    if (wrap) wrap.innerHTML = renderDayCloseReceipt(activeDate);
  }
}

export function setDayCloseDate(date) {
  if (!date) return;
  _state.date = date;
  loadAndRenderDayClose();
}

export function setDayCloseExportMode(mode) {
  if (!['day','month','year','custom'].includes(mode)) return;
  _state.exportMode = mode;
  if (mode === 'custom' && !_state.exportFrom) {
    _state.exportFrom = _state.date || todayISO();
    _state.exportTo   = _state.date || todayISO();
  }
  loadAndRenderDayClose();
}

export function setDayCloseExportCustom(which, value) {
  if (!value) return;
  if (which === 'from') _state.exportFrom = value;
  if (which === 'to')   _state.exportTo   = value;
  loadAndRenderDayClose();
}

export function printDayClose() {
  // Backward-Compat-Pfad — der UI-Button öffnet jetzt das Beleg-Aktion-Modal.
  // Diese Funktion bleibt für Tests + Direkt-Aufrufe (z.B. Drucker-Adapter).
  printDayCloseReceipt(_state.date || todayISO());
}

export async function exportDayCloseDatev() {
  const range = resolveExportRange();
  try {
    await exportOrdersDatevCsv({ from: range.from, to: range.to, label: range.label });
  } catch (e) {
    console.error('[exportDayCloseDatev]', e);
    if (typeof window.showToast === 'function') {
      window.showToast('DATEV-Export fehlgeschlagen: ' + (e.message || e), 'error');
    }
  }
}

export async function exportDayCloseDsfinvk() {
  const range = resolveExportRange();
  try {
    await exportDsfinvk({ from: range.from, to: range.to, label: range.label });
  } catch (e) {
    console.error('[exportDayCloseDsfinvk]', e);
    if (typeof window.showToast === 'function') {
      window.showToast('DSFinV-K-Export fehlgeschlagen: ' + (e.message || e), 'error');
    }
  }
}
